import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
// Import Types
import type { GroupedOption } from 'components/common/PopoverWithOptionList/types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectPartnerDataSourceId, resetPartnerSettings } from 'store/slices/partnerSettings';
import { selectUserActivePartner, setUserDataFetch } from 'store/slices/userData';
import { selectHeaderNavigation, selectHeaderPartners } from 'store/slices/header';
// Import Components
import PartnersOptionList from 'components/common/PartnersOptionList';
// Import UI Components
import { Button, Popover, Tooltip } from '@geneui/components';
// Import Services
import { Helpers } from 'services';
import { AuthHttpService } from 'services/http';
import { publish } from 'services/event';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useOnClickOutside from 'hooks/UseOnClickOutside';
// Import Constants
import { l, GlobalEvents } from 'constants/common';

const { mapToPartnerGroupsModel } = Helpers;

const Partners = ({ setIsLoading, setPartnersVisibilityStatus, isVisible, toggleHandler }) => {
    const { t } = useTranslation();
    const { goToRoute } = useGoToRoute();
    const dispatch = useAppDispatch();

    const { route: path } = useAppSelector(selectHeaderNavigation);
    const partners = useAppSelector(selectHeaderPartners);
    const ActivePartner = useAppSelector(selectUserActivePartner);
    const partnerDataSourceId = useAppSelector(selectPartnerDataSourceId);

    const PartnerName = ActivePartner?.Name;
    const optionListRef = useRef(null);
    const PartnerId = ActivePartner?.PartnerId;
    const [partnersFullListGroupModel, setPartnersFullListGroupModel] = useState<GroupedOption[]>([]);

    useEffect(() => {
        if (!isEmpty(partners)) {
            const partnerGroupsModelData = mapToPartnerGroupsModel(partners, partnerDataSourceId);
            setPartnersFullListGroupModel(partnerGroupsModelData);
        }
    }, [partners, partnerDataSourceId]);

    const selectPartner = ({ value: partnerId }) => {
        if (PartnerId === partnerId) return;

        setPartnersVisibilityStatus(false);
        setIsLoading(true);

        AuthHttpService.changePartner(partnerId).then(() => {
            dispatch(resetPartnerSettings());
            dispatch(setUserDataFetch());
            publish(GlobalEvents.PartnerChanged, partnerId);
            goToRoute({ path });
        });
    };

    useOnClickOutside(
        optionListRef,
        useCallback(() => {
            if (isVisible) {
                setPartnersVisibilityStatus(false);
            }
        }, [isVisible, setPartnersVisibilityStatus]),
    );

    return (
        <div ref={optionListRef}>
            <Popover
                isOpen={isVisible}
                extendTargetWidth={false}
                minHeight={332}
                maxHeight={352}
                scrollbarNeeded={false}
                align="end"
            >
                <div className="crm-option-list-content-wrapper partner-popover-content navigation-popover-content">
                    <div className="partners-button-wrap">
                        <Tooltip text={PartnerName} position="bottom">
                            <Button
                                className="crm-option-list-content__toggle-button option-list-partner-button"
                                onClick={toggleHandler}
                                icon={isVisible ? 'bc-icon-arrow-up' : 'bc-icon-arrow-down'}
                            >
                                {isEmpty(PartnerName) ? t(l.SelectPartner) : PartnerName}
                            </Button>
                        </Tooltip>
                    </div>
                    {isVisible && (
                        <PartnersOptionList
                            groupsListData={partnersFullListGroupModel}
                            titles={[t(l.Partner), t(l.ID)]}
                            selectHandler={selectPartner}
                            selectedPartner={PartnerId}
                        />
                    )}
                </div>
            </Popover>
        </div>
    );
};

Partners.propTypes = {
    setIsLoading: PropTypes.func.isRequired,
    setPartnersVisibilityStatus: PropTypes.func.isRequired,
    isVisible: PropTypes.bool.isRequired,
    toggleHandler: PropTypes.func.isRequired,
};

Partners.defaultProps = {
    isVisible: false,
};

export default memo(Partners);
